import { useQuery } from '@tanstack/react-query';
import { BookmarkPlus, Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Button, Modal, fieldCls, inputCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { useRole } from '../../lib/role';
import type { OpenDay } from './ExampleDay';
import { usePin } from './pin';
import { LABEL, T } from './texts';

/*
 * Окно «Выбрать день» (004, T064; contracts/ui.md, «Окна»; FR-022): человек — поиск в области роли
 * (`GET /api/rules/people`, от двух букв, до 20 человек), дата — не позже сегодня. «Показать день» отдаёт выбор
 * примеру: он покажет день «Сейчас» и «После публикации».
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/u;
const MIN_QUERY = 2;

export function PickDayDialog({
  open,
  date,
  onPick,
  onClose,
}: {
  open: boolean;
  /** Дата по умолчанию и самая поздняя — сегодня по объекту. */
  date: string;
  onPick: (day: OpenDay) => void;
  onClose: () => void;
}) {
  const { role } = useRole();
  const pin = usePin();
  const [query, setQuery] = useState('');
  const [person, setPerson] = useState<{ personId: string; name: string } | null>(null);
  const [day, setDay] = useState(date);
  const ids = useId();

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPerson(null);
    setDay(date);
  }, [open, date]);

  const q = query.trim();
  const people = useQuery({
    queryKey: rulesKeys.people(role, q),
    queryFn: () => api.rulesPeople(q),
    enabled: open && q.length >= MIN_QUERY,
    staleTime: 60_000,
  });
  const ready = person !== null && ISO.test(day) && day <= date;

  return (
    <Modal open={open} onClose={onClose} title={LABEL.pickDay} width="w-[520px]">
      <div className="space-y-4">
        <div>
          <label htmlFor={`${ids}-q`} className="mb-1.5 block text-[13px] font-medium text-ink">
            {LABEL.person}
          </label>
          <div className="relative flex items-center">
            <Search
              size={16}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-2.5 text-ink-3"
              aria-hidden
            />
            <input
              id={`${ids}-q`}
              type="search"
              data-autofocus
              autoComplete="off"
              aria-label={LABEL.person}
              aria-describedby={`${ids}-hint`}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPerson(null);
              }}
              className={`${inputCls} pl-8`}
            />
          </div>
          <p id={`${ids}-hint`} className="mt-1 text-[12px] text-ink-3">
            {q.length < MIN_QUERY
              ? T.personHint
              : people.error
                ? T.peopleError
                : people.data && people.data.length === 0
                  ? T.noPeople
                  : ''}
          </p>
          {q.length >= MIN_QUERY && people.data && people.data.length > 0 && (
            <div
              role="listbox"
              tabIndex={-1}
              aria-label={LABEL.people}
              className="mt-1 max-h-[240px] overflow-y-auto rounded-control border border-line"
            >
              {people.data.map((p) => {
                const on = person?.personId === p.personId;
                const pick = () => setPerson(p);
                return (
                  <div
                    key={p.personId}
                    role="option"
                    aria-selected={on}
                    tabIndex={0}
                    onClick={pick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        pick();
                      }
                    }}
                    className={`flex min-h-9 cursor-pointer items-center px-3 text-[13.5px] max-sm:min-h-10 ${
                      on ? 'bg-accent-soft font-medium text-accent' : 'text-ink hover:bg-subtle'
                    }`}
                  >
                    {p.name}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label htmlFor={`${ids}-d`} className="mb-1.5 block text-[13px] font-medium text-ink">
            {LABEL.dayDate}
          </label>
          <input
            id={`${ids}-d`}
            type="date"
            max={date}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className={`${fieldCls} w-[170px] tabular-nums max-sm:w-full`}
          />
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
        {/* Администратору правил — закрепить выбранный день проверочным примером (FR-051). */}
        {pin && (
          <Button
            variant="ghost"
            icon={BookmarkPlus}
            disabled={!ready}
            className="mr-auto max-sm:mr-0 max-sm:w-full"
            onClick={() => {
              if (!person || !ready) return;
              // Окно примера встаёт вместо этого окна: один слой поверх страницы.
              onClose();
              pin({ personId: person.personId, name: person.name, date: day });
            }}
          >
            {LABEL.pin}
          </Button>
        )}
        <Button onClick={onClose}>{LABEL.cancel}</Button>
        <Button
          variant="primary"
          disabled={!ready}
          onClick={() => {
            if (person && ready) onPick({ personId: person.personId, name: person.name, date: day });
          }}
        >
          {LABEL.showDay}
        </Button>
      </div>
    </Modal>
  );
}
